import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { isAxiosError } from 'axios';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getUserProfile } from '@/apis/users';
import { Client } from '@/types/clientTypes';
import { toGenderDisplay } from '@/utils/gender';

const MS_PER_YEAR = 31557600000;

const mapProfileToClient = (
  profile: Record<string, any>,
  clientId: string,
  username?: string | null
): Client => ({
  id: profile.user_profile_id ?? clientId,
  client_id: profile.client_id ?? profile.user_profile_id ?? clientId,
  username: username || profile.username || null,
  name:
    profile.first_name && profile.last_name
      ? `${profile.first_name} ${profile.last_name}`
      : 'Unknown',
  first_name: profile.first_name || '',
  last_name: profile.last_name || '',
  title: profile.title || '',
  gender: toGenderDisplay(profile.gender),
  age: profile.date_of_birth
    ? Math.floor((new Date().getTime() - new Date(profile.date_of_birth).getTime()) / MS_PER_YEAR)
    : 0,
  date_of_birth: profile.date_of_birth || '',
  phone_number: profile.phone_number || 'Not specified',
  alternate_phone: profile.alternate_phone,
  alternate_email: profile.alternate_email,
  preferred_contact_method: profile.preferred_contact_method || 'phone',
  city: profile.city || 'Not specified',
  state: profile.state,
  country: profile.country,
  zipcode: profile.zipcode,
  address_line_1: profile.address_line_1,
  address_line_2: profile.address_line_2,
  house_no: profile.house_no ?? null,
  street: profile.street ?? null,
  landmark: profile.landmark ?? null,
  image: '/avatars/default.png',
  profile_picture: profile.profile_picture,
  email: profile.email,
  role: profile.role || 'client',
  occupation: profile.occupation || 'Not specified',
  relationship_status: profile.relationship_status || 'Not specified',
  relationship_status_custom: profile.relationship_status_custom || null,
  organization: profile.organization,
  clinic: profile.clinic,
  emergency_contact_name: profile.emergency_contact_name,
  emergency_contact_relation: profile.emergency_contact_relation,
  emergency_contact_number: profile.emergency_contact_number,
  appointment_duration: profile.appointment_duration || '00:30:00',
  health_goals: profile.health_goals || {},
  client_story: profile.client_story || {},
  lifestyle_history: profile.lifestyle_history || {},
  family_history: profile.family_history || {},
  abha_number: profile.abha_number || null,
  aadhar_number: profile.aadhar_number || null,
  hipaa_consent: profile.hipaa_consent || false,
  hipaa_consent_date: profile.hipaa_consent_date || null,
  hipaa_consent_version: profile.hipaa_consent_version || null,
  program_names: profile.program_names || [],
  addon_names: profile.addon_names || [],
  membership_names: profile.membership_names || [],
});

const fetchClientById = async (clientId: string, accessToken: string) => {
  try {
    const profileResponse = await getUserProfile(clientId, accessToken);
    const profile = profileResponse.data;
    if (profile && Object.keys(profile).length > 0) {
      return mapProfileToClient(profile, clientId, profile.username);
    }
  } catch (error) {
    if (!isAxiosError(error) || error.response?.status !== 404) {
      throw error;
    }
  }

  const response = await axios.get(`users/users/${clientId}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  const profile = response.data?.Profile;
  if (!profile) {
    throw new Error('Profile data is missing');
  }

  return mapProfileToClient(profile, clientId, response.data?.username);
};

/**
 * Custom hook to handle client data for member pages
 * Works for both CLIENT users (with selectedClient) and ADMIN/SUPER_ADMIN users (fetches from API)
 */
export const useMemberClient = (
  currentClient: Client | null = null,
  selectedClient: Client | null = null,
  clientIdFromUrl?: string
) => {
  const { clientId: routeClientId } = useParams<{ clientId: string }>();
  const [fetchedClient, setFetchedClient] = useState<Client | null>(null);
  const [isLoadingClient, setIsLoadingClient] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const providedClient = currentClient || selectedClient;
  const clientId = clientIdFromUrl || routeClientId;
  const providedClientId = providedClient?.id;

  useEffect(() => {
    const abortController = new AbortController();

    const fetchClientData = async () => {
      if (providedClient) {
        setIsLoadingClient(false);
        setFetchedClient(null);
        return;
      }

      if (!clientId) {
        setFetchedClient(null);
        setIsLoadingClient(false);
        setError(null);
        return;
      }

      setIsLoadingClient(true);
      setError(null);

      try {
        const { accessToken } = (await fetchAuthSession()).tokens ?? {};
        if (!accessToken) {
          setError('Authentication required');
          return;
        }

        const clientData = await fetchClientById(clientId, accessToken.toString());
        if (!abortController.signal.aborted) {
          setFetchedClient(clientData);
        }
      } catch (err) {
        if (abortController.signal.aborted || axios.isCancel(err)) {
          return;
        }
        setError('Failed to load client data');
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingClient(false);
        }
      }
    };

    fetchClientData();

    return () => {
      abortController.abort();
    };
  }, [providedClientId, clientId]);

  const resolvedClient = providedClient || fetchedClient;

  return {
    client: resolvedClient,
    isLoading: isLoadingClient,
    error,
  };
};
